import { isEmpty, has, split, get, sortBy } from 'lodash';
import { amountDecimalPoint } from '../config';

const sortCodeRegEx = /(\d{2})(?=\d)/g;
const creditCardRegEx = /(\*){12}/g;
const creditCardDisplayMask = '************';

export const formatSortCode = (sortCode = '') => {
	const result = sortCode.length === 6 ? sortCode.replace(sortCodeRegEx, '$1-') : sortCode;
	return result;
};

export const formatAccountDetailsLine = (sortCode, accountNumber) => {
	const parts = [formatSortCode(sortCode), accountNumber];
	return parts.filter((x) => x).join(' | ');
};

export const filterSelected = (data, idsToFilter) => data.filter(({ id }) => !idsToFilter.includes(id));

export const formatAccountToView = ({
	accountHolder = '',
	accountNumber = '',
	sortCode = '',
	displayName = '',
	name = '',
	type = '',
	number = '',
}) => {
	const accountName = displayName || name;
	if (accountHolder) {
		return `${accountHolder} | ${accountName} | ${formatSortCode(sortCode)} | ${accountNumber}`;
	}
	if (type === 'credit_card') {
		return `${accountName} | ${number}`;
	}
	return `${accountName} | ${formatSortCode(sortCode)} | ${accountNumber}`;
};

// TODO: isAddPayeeEnable will be handled by WithAddPayee container when Add Payee comes in place, for now Its just placed in utils section.
export const isAddPayeeEnable = (accounts, fromAccountId) => {
	if (fromAccountId) {
		const account = accounts.find((account) => account.id === fromAccountId);
		return account.actionsAvailable['/account/beneficiaries/account/readwrite'];
	}
	return accounts.some((account) => account.actionsAvailable['/account/beneficiaries/account/readwrite']);
};
export const CAPABILITIES = {
	FPS: '/account/payments/uk/default/out',
	SO: '/account/mandates/so/write',
	IFT: '/account/payments/ift/out',
	CHAPS: '/account/payments/chaps/out',
	BACS: '/account/payments/bacscredit/out',
	BACSDEBIT: '/account/payments/bacsdebit/out',
	EFFECTIVEWARNING: '/account/payments/effectivewarning',
};

const getBalance = (account, type) => {
	const balances = account.balances || [];
	const filteredBalances = balances.filter((balance) => balance.type === type);
	if (filteredBalances.length > 0) {
		return filteredBalances[0].amount.value;
	}
	return '';
};

const getBalanceCurrency = (accountBalances = [], type) => {
	const filteredBalances = accountBalances && accountBalances.filter((balance) => balance.type === type);
	if (filteredBalances && filteredBalances.length > 0) {
		return get(filteredBalances[0], 'amount.currency', '');
	}
	return '';
};

export const extractBalances = (account) => ({
	availableBalance: getBalance(account, 'available') || 0,
	currentBalance: getBalance(account, 'current') || 0,
	availableBalanceCurrency: getBalanceCurrency(account.balances, 'available'),
	currentBalanceCurrency: getBalanceCurrency(account.balances, 'current'),
});

export const formatFavourites = (accounts = []) =>
	accounts.map((acc) => ({
		...acc,
		...extractBalances(acc),
		isFavourite: true,
	}));

// prettier-ignore
const formatCreditCard = creditcard =>
	(creditcard !== null
		? `${creditcard.slice(0, 4)} ${creditcard.slice(4, 8)} ${creditcard.slice(8, 12)} ${creditcard.slice(-4)}`
		: null);

export const formatCreditCardNumber = (cardNumber) => {
	const [number] = cardNumber.replace(creditCardRegEx, creditCardDisplayMask).split('-');
	return ` ${formatCreditCard(number)}`;
};

// prettier-ignore
export const balanceInPence = balance => balance * (10 ** amountDecimalPoint);

export const showAvailableBalance = (account) => {
	if (get(account.actionsAvailable, '/account/balance/available', '')) {
		return balanceInPence(account.availableBalance);
	}
	return '';
};

export const parseAccountListResponse = (accounts = []) => {
	const accountList = accounts.map((account, index) => {
		const displayName = get(account, 'metadata.display_name', '');
		const accountName = get(account, 'metadata.account_name', '');
		const displayOrder = get(account, 'metadata.display_order', 0);
		const productName = get(account, 'product.name', '');
		const number = split(account.number, '-');
		return {
			id: account.id,
			type: account.type,
			number: account.number,
			accountName,
			sortCode: number[0],
			accountNumber: number[1],
			product: account.product,
			actionsAvailable: account.actions_available,
			bank_id: account.bank_id,
			metadata: account.metadata,
			displayName: isEmpty(displayName) ? productName : displayName,
			display_order: displayOrder === 0 ? index + 1 : displayOrder,
			bacs: has(account, 'bacs'),
		};
	});
	return sortBy(accountList, ['display_order']);
};

export const hasAccountInAccounts = (fromAccountIds, fromAccounts) =>
	fromAccountIds.every((accountId) => {
		let allowed = false;

		fromAccounts.forEach((account) => {
			if (account.id === accountId) {
				allowed = true;
			}
		});
		return allowed;
	});
